import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react';
import { PageGuard } from '@components/PageGuard';
import { ledgerService } from '@services/ledger.service';
import { cn, formatCurrency } from '@lib/utils';
import { formatTimestamp, humanizeCode, shortId } from './ledgerFormat';

const thClass =
  'text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] whitespace-nowrap';

const tdClass = 'px-6 py-3 text-[14px] text-[var(--text-primary)] whitespace-nowrap';

interface AnomalyCardProps {
  title: string;
  /** One sentence: what the check means and why it matters. */
  explanation: string;
  count: number;
  emptyText: string;
  children: ReactNode;
}

const AnomalyCard = ({ title, explanation, count, emptyText, children }: AnomalyCardProps) => {
  const clean = count === 0;
  return (
    <section className="rounded-3xl bg-[var(--bg-surface)] shadow-soft overflow-hidden">
      <div className="px-6 py-5 border-b border-[var(--border-subtle)] flex items-start justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">{title}</h2>
          <p className="text-[13px] text-[var(--text-muted)]">{explanation}</p>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold shrink-0',
            clean
              ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
              : 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
          )}
        >
          {clean ? (
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
          ) : (
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} />
          )}
          {count}
        </span>
      </div>

      {clean ? (
        <div className="px-6 py-10 text-center text-[14px] text-[var(--text-muted)]">
          {emptyText}
        </div>
      ) : (
        <div className="overflow-x-auto">{children}</div>
      )}
    </section>
  );
};

const walletLink = (walletId: string, label: string | null, typeCode: string) => (
  <Link
    to={`/finance/wallets/${walletId}`}
    className="text-[var(--text-accent)] hover:underline"
  >
    {label ?? humanizeCode(typeCode)}
  </Link>
);

export const LedgerAnomaliesPage = () => {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['ledger', 'anomalies'],
    queryFn: () => ledgerService.getAnomalies(),
  });

  const checks = data?.checks;

  return (
    <PageGuard permission="wallets.read">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
              Finance
            </p>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Ledger health
            </h1>
            <p className="text-[14px] text-[var(--text-muted)]">
              Why the numbers do not add up. Each check below isolates one way money
              can go missing, and every row links to the wallet it came from.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {data && (
              <span className="text-[13px] text-[var(--text-muted)]">
                Checked {formatTimestamp(data.generatedAt)}
              </span>
            )}
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-50 transition-all duration-200"
            >
              <RefreshCw
                className={cn('w-4 h-4', isFetching && 'animate-spin')}
                strokeWidth={1.5}
              />
              Re-run checks
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-12 text-center text-[14px] text-[var(--text-muted)]">
            Running reconciliation checks...
          </div>
        )}

        {!!error && !isLoading && (
          <div className="rounded-3xl bg-[var(--bg-surface)] shadow-soft p-12 text-center text-[14px] text-[var(--state-danger)]">
            Failed to run reconciliation checks.
          </div>
        )}

        {checks && (
          <div className="space-y-6">
            {/* 1. Raw available below zero. NOT a fault on its own: escrow
                records each party's share as a hold on the DESTINATION wallet
                while the cash stays in chekit_escrow, so the earmark is
                subtracted from a balance that never contained it. The
                `Stranded` column is the part that is genuinely wrong. */}
            <AnomalyCard
              title="Raw available below zero"
              explanation="Available balance is the DB's generated column (balance − held). Chekit's escrow marks a payout as HELD on the destination wallet before the money lands there, so a negative here is normally just an incoming earmark. Read the Stranded column: that is money held against a basket that already finished, which nothing will ever release."
              count={checks.negativeAvailable.count}
              emptyText="No wallet has a negative raw available balance."
            >
              <table className="w-full">
                <thead className="bg-[var(--bg-base)]">
                  <tr>
                    <th className={thClass}>Wallet</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Balance</th>
                    <th className={thClass}>Incoming (earmarked)</th>
                    <th className={thClass}>Stranded</th>
                    <th className={thClass}>Spendable</th>
                    <th className={thClass}>Raw available</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {checks.negativeAvailable.rows.map((row) => (
                    <tr key={row.walletId} className="hover:bg-[var(--bg-base)] transition-colors">
                      <td className={cn(tdClass, 'font-medium')}>
                        {walletLink(row.walletId, row.ownerLabel, row.typeCode)}
                      </td>
                      <td className={cn(tdClass, 'text-[var(--text-muted)]')}>{row.typeCode}</td>
                      <td className={tdClass}>{formatCurrency(row.balance)}</td>
                      <td className={cn(tdClass, 'text-[var(--text-secondary)]')}>
                        {formatCurrency(row.heldIncoming)}
                      </td>
                      <td
                        className={cn(
                          tdClass,
                          row.heldStranded > 0
                            ? 'text-[var(--state-danger)] font-semibold'
                            : 'text-[var(--text-muted)]',
                        )}
                      >
                        {formatCurrency(row.heldStranded)}
                      </td>
                      <td className={cn(tdClass, 'font-medium')}>
                        {formatCurrency(row.spendable)}
                      </td>
                      <td className={cn(tdClass, 'text-[12px] text-[var(--text-muted)]')}>
                        {formatCurrency(row.availableBalance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AnomalyCard>

            {/* 2. Unbalanced journal entries */}
            <AnomalyCard
              title="Unbalanced journal entries"
              explanation="In double-entry bookkeeping every entry's debits must equal its credits — an entry where they differ means money was created or destroyed inside the ledger."
              count={checks.unbalancedJournalEntries.count}
              emptyText="Every journal entry balances. Debits equal credits across the ledger."
            >
              <table className="w-full">
                <thead className="bg-[var(--bg-base)]">
                  <tr>
                    <th className={thClass}>Entry</th>
                    <th className={thClass}>Description</th>
                    <th className={thClass}>Debits</th>
                    <th className={thClass}>Credits</th>
                    <th className={thClass}>Difference</th>
                    <th className={thClass}>Posted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {checks.unbalancedJournalEntries.rows.map((row) => (
                    <tr key={row.entryId} className="hover:bg-[var(--bg-base)] transition-colors">
                      <td className={cn(tdClass, 'font-mono text-[13px]')}>
                        {shortId(row.entryId)}
                      </td>
                      <td className="px-6 py-3 text-[14px] text-[var(--text-secondary)]">
                        <span className="block max-w-[360px] truncate">
                          {row.description ?? '—'}
                        </span>
                      </td>
                      <td className={tdClass}>{formatCurrency(row.debitTotal)}</td>
                      <td className={tdClass}>{formatCurrency(row.creditTotal)}</td>
                      <td className={cn(tdClass, 'text-[var(--state-danger)] font-semibold')}>
                        {formatCurrency(row.debitTotal - row.creditTotal)}
                      </td>
                      <td className={cn(tdClass, 'text-[var(--text-muted)]')}>
                        {formatTimestamp(row.postedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AnomalyCard>

            {/* 3. Held balance drift */}
            <AnomalyCard
              title="Held balance drift"
              explanation="The held figure stored on a wallet should equal its holds minus its releases, settlements, commissions and delivery fees — a gap means the wallet row and its transaction history disagree about what is locked."
              count={checks.heldBalanceDrift.count}
              emptyText="Every wallet's stored held balance matches its transaction history exactly."
            >
              <table className="w-full">
                <thead className="bg-[var(--bg-base)]">
                  <tr>
                    <th className={thClass}>Wallet</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Stored held</th>
                    <th className={thClass}>Recomputed held</th>
                    <th className={thClass}>Drift</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {checks.heldBalanceDrift.rows.map((row) => (
                    <tr key={row.walletId} className="hover:bg-[var(--bg-base)] transition-colors">
                      <td className={cn(tdClass, 'font-medium')}>
                        {walletLink(row.walletId, row.ownerLabel, row.typeCode)}
                      </td>
                      <td className={cn(tdClass, 'text-[var(--text-muted)]')}>{row.typeCode}</td>
                      <td className={tdClass}>{formatCurrency(row.storedHeld)}</td>
                      <td className={tdClass}>{formatCurrency(row.recomputedHeld)}</td>
                      <td className={cn(tdClass, 'text-[var(--state-danger)] font-semibold')}>
                        {formatCurrency(row.drift)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AnomalyCard>

            {/* 4. Stale holds */}
            <AnomalyCard
              title="Stale holds"
              explanation="Money still held against a basket that has already finished — confirmed, cancelled, rejected, refunded or returned — so nothing will ever release it on its own."
              count={checks.staleHolds.count}
              emptyText="No money is held against a finished basket."
            >
              <table className="w-full">
                <thead className="bg-[var(--bg-base)]">
                  <tr>
                    <th className={thClass}>Wallet</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Basket</th>
                    <th className={thClass}>Basket status</th>
                    <th className={thClass}>Still held</th>
                    <th className={thClass}>Held since</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {checks.staleHolds.rows.map((row) => (
                    <tr
                      key={`${row.walletId}-${row.basketId}`}
                      className="hover:bg-[var(--bg-base)] transition-colors"
                    >
                      <td className={cn(tdClass, 'font-medium')}>
                        {walletLink(row.walletId, row.ownerLabel, row.typeCode)}
                      </td>
                      <td className={cn(tdClass, 'text-[var(--text-muted)]')}>{row.typeCode}</td>
                      <td className={cn(tdClass, 'font-mono text-[13px] text-[var(--text-muted)]')}>
                        {shortId(row.basketId)}
                      </td>
                      <td className={cn(tdClass, 'text-[var(--text-secondary)]')}>
                        {row.basketStatus}
                      </td>
                      <td className={cn(tdClass, 'text-[var(--state-warning)] font-semibold')}>
                        {formatCurrency(row.amount)}
                      </td>
                      <td className={cn(tdClass, 'text-[var(--text-muted)]')}>
                        {formatTimestamp(row.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AnomalyCard>

            <p className="text-[12px] text-[var(--text-muted)]">
              Each check returns at most 200 rows. Basket ids are per-merchant baskets
              inside an order — open the order from the Orders queue to trace its money.
            </p>
          </div>
        )}
      </div>
    </PageGuard>
  );
};
